import { queryOptions } from "@tanstack/react-query";
import { UserService } from "../service/UserService";
import { FolderService } from "../service/FolderService";
import { Folder } from "../util/Types/Folder";
import { Space } from "../util/Types/Sidebar";
import { normalizeFolders, normalizeSpaces } from "../util/sidebarNormalizer";

type FolderRef = { id: string; parentId?: string | null };

const BATCH_FOLDER_DETAILS_LIMIT = 400;

const dedupeFolderRefs = (refs: FolderRef[]): FolderRef[] => {
  const byId = new Map<string, FolderRef>();
  for (const ref of refs) {
    if (!byId.has(ref.id)) {
      byId.set(ref.id, ref);
    }
  }
  return [...byId.values()];
};

const fetchFolderDetailsByRefs = async (
  refs: FolderRef[],
): Promise<Map<string, Folder>> => {
  const unique = dedupeFolderRefs(refs);
  const map = new Map<string, Folder>();
  for (let i = 0; i < unique.length; i += BATCH_FOLDER_DETAILS_LIMIT) {
    const chunk = unique
      .slice(i, i + BATCH_FOLDER_DETAILS_LIMIT)
      .map((r) => r.id);
    if (chunk.length === 0) {
      continue;
    }
    const res = await FolderService.getBatchDetails(chunk);
    const folders = res.data.folders;
    for (const [id, folder] of Object.entries(folders)) {
      map.set(id, folder);
    }
  }
  return map;
};

const refsToFolders = (refs: FolderRef[], byId: Map<string, Folder>): Folder[] =>
  refs.flatMap((ref) => {
    const loaded = byId.get(ref.id);
    if (!loaded) {
      return [];
    }
    const row: Folder = {
      ...loaded,
      parentId: loaded.parentId ?? ref.parentId ?? null,
      folders: [],
    };
    return [row];
  });

const buildFolderTree = (flatFolders: Folder[]): Folder[] => {
  const folderMap = new Map<string, Folder>();
  flatFolders.forEach((f) => {
    folderMap.set(f.id, { ...f, folders: [] });
  });

  const rootFolders: Folder[] = [];
  folderMap.forEach((folder) => {
    if (folder.parentId && folderMap.has(folder.parentId)) {
      folderMap.get(folder.parentId)!.folders.push(folder);
    } else {
      rootFolders.push(folder);
    }
  });

  return rootFolders;
};

export const sidebarQueries = {
  folders: () =>
    queryOptions({
      queryKey: ["sidebar", "folders"],
      queryFn: async () => {
        const { data } = await FolderService.getFolders();
        const folderRefs: FolderRef[] = data || [];
        const byId = await fetchFolderDetailsByRefs(folderRefs);
        const validFolders = refsToFolders(folderRefs, byId);
        const rootFolders = buildFolderTree(validFolders);
        return normalizeFolders(rootFolders);
      },
    }),

  spaces: () =>
    queryOptions({
      queryKey: ["sidebar", "spaces"],
      queryFn: async () => {
        const { data } = await UserService.getSpaces();
        const spaces = (data ?? []) as Space[];
        const allRefs = spaces.flatMap((s) => s.folder ?? []);
        const byId = await fetchFolderDetailsByRefs(allRefs);
        const spacesWithFolders = spaces.map((space) => {
          const refs = space.folder ?? [];
          const validFolders = refsToFolders(refs, byId);
          const rootFolders = buildFolderTree(validFolders);
          return {
            ...space,
            folder: rootFolders,
          };
        });
        return normalizeSpaces(spacesWithFolders);
      },
    }),

  all: () =>
    queryOptions({
      queryKey: ["sidebar"],
      queryFn: async () => {
        const { data: foldersData } = await FolderService.getFolders();
        const folderRefs: FolderRef[] = foldersData || [];

        const { data: spacesData } = await UserService.getSpaces();
        const spaces = (spacesData ?? []) as Space[];
        const spaceFolderRefs = spaces.flatMap((s) => s.folder ?? []);
        const mergedRefs = dedupeFolderRefs([...folderRefs, ...spaceFolderRefs]);
        const byId = await fetchFolderDetailsByRefs(mergedRefs);

        const validFolders = refsToFolders(folderRefs, byId);
        const rootFolders = buildFolderTree(validFolders);
        const normalizedFolders = normalizeFolders(rootFolders);

        const spacesWithFolders = spaces.map((space) => {
          const refs = space.folder ?? [];
          const spaceValidFolders = refsToFolders(refs, byId);
          const spaceRootFolders = buildFolderTree(spaceValidFolders);
          return {
            ...space,
            folder: spaceRootFolders,
          };
        });
        const normalizedSpaces = normalizeSpaces(spacesWithFolders);

        return {
          folders: normalizedFolders,
          spaces: normalizedSpaces,
        };
      },
    }),
};
